import { ReactNode, createContext, useContext, useMemo, useReducer, useCallback } from 'react'
import Cookies from 'js-cookie'
import { jwtDecode } from 'jwt-decode'
import { PinTokenType } from '../../../types/portalTypes/PinTokenType'

const JWT_TOKEN_NAME = 'pin_user_jwt_token'

const PinTokenContext = createContext<ReturnType<typeof usePinTokenValue> | undefined>(undefined)

function getUserOrderKeyNotificationId() {
  const path = window.location.pathname
  const pathSegments = path.split('/').filter(Boolean)

  const userOrderKey = pathSegments[pathSegments.length - 1]
  const notificationId = pathSegments[pathSegments.length - 2]

  return `${notificationId}-${userOrderKey}`
}

function removeAllJWTTokens() {
  const allCookies = Cookies.get()
  Object.keys(allCookies).forEach(cookieName => {
    if (cookieName.startsWith(JWT_TOKEN_NAME)) {
      Cookies.remove(cookieName)
    }
  })
}

function usePinTokenValue() {
  const jwtCookieName = JWT_TOKEN_NAME + getUserOrderKeyNotificationId()

  //Force rerender when token changes so I use 'version' in useMemo deps
  const [version, forceUpdate] = useReducer(x => x + 1, 0)

  const getJWT = useCallback(() => {
    return Cookies.get(jwtCookieName) || null
  }, [jwtCookieName])
  
  const handleSetPinToken = useCallback((newPinToken: string | null) => {
    if (newPinToken) {
      const decoded = jwtDecode<PinTokenType>(newPinToken)
      const expiryDate = new Date(decoded.exp * 1000)
      Cookies.set(jwtCookieName, newPinToken, {
        expires: expiryDate,
        sameSite: 'strict',
      })
    } else {
      removeAllJWTTokens()
    }
    forceUpdate()
  }, [jwtCookieName])

  return useMemo(() => ({
    get pinToken() {
      return getJWT()
    },
    setPinToken: handleSetPinToken,
  // eslint-disable-next-line react-hooks/exhaustive-deps -- I need version in here to force a rerender when the token changes.
  }), [handleSetPinToken, getJWT, version])
}

export function usePinTokenState() {
  const context = useContext(PinTokenContext)
  if (!context) {
    throw new Error('usePinTokenState must be used within PinTokenStateProvider')
  }
  return context
}

export function PinTokenStateProvider({ children }: { children: ReactNode }) {
  const value = usePinTokenValue()
  return (
    <PinTokenContext.Provider value={value}>
      {children}
    </PinTokenContext.Provider>
  )
}

